import React, { createContext, useCallback, useContext, useState } from "react";
import { Check, CircleAlert, X } from "lucide-react";

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message, type = "success") => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setToasts((current) => [...current, { id, message, type }]);
      window.setTimeout(() => dismiss(id), 5000);
      return id;
    },
    [dismiss]
  );

  const toast = {
    show,
    success: (message) => show(message, "success"),
    error: (message) => show(message, "error"),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function Toaster({ toasts, onDismiss }) {
  return (
    <div
      className="fixed z-[100] bottom-6 inset-x-4 sm:inset-x-auto sm:right-6 sm:left-auto flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
      aria-relevant="additions"
    >
      <div>
        {toasts.map((item) => {
          const isSuccess = item.type === "success";

          return (
            <div
              key={item.id}
              role="status"
              className={`toast-in pointer-events-auto w-full sm:w-[360px] rounded-2xl px-4 py-3.5 flex items-start gap-3 shadow-[0_16px_40px_rgba(192,60,160,0.28)] ${
                isSuccess
                  ? "bg-[#C03CA0] text-white"
                  : "bg-white text-gray-900 border-2 border-[#C03CA0]"
              }`}
            >
              <span
                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  isSuccess ? "bg-white/20 text-white" : "bg-[#C03CA0] text-white"
                }`}
              >
                {isSuccess ? <Check size={16} strokeWidth={2.5} /> : <CircleAlert size={16} />}
              </span>

              <p className="flex-1 text-sm font-medium leading-relaxed pt-1">
                {item.message}
              </p>

              <button
                type="button"
                onClick={() => onDismiss(item.id)}
                className={`mt-0.5 transition ${
                  isSuccess ? "text-white/70 hover:text-white" : "text-gray-400 hover:text-[#C03CA0]"
                }`}
                aria-label="Dismiss notification"
              >
                <X size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
